import type { ChatMessage, Content } from "@tsmono/inspect-common/types";
import { isRecord } from "@tsmono/util";

export type ChatViewToolCallStyle = "compact" | "complete" | "omit";

export interface ChatViewDisplayOptions {
  /** Indent message content. Default: false. */
  indented?: boolean;
  /** Roles whose header row is hidden. */
  unlabeledRoles?: string[];
  /** Format timestamps shown on messages. When absent, timestamps are hidden. */
  formatDateTime?: (date: Date) => string;
  /** Apply a background color to user messages. Default: true. */
  highlightUserMessage?: boolean;
}

export interface ChatViewLabelOptions {
  /** Custom labels keyed by message id (e.g. citation refs). */
  messageLabels?: Record<string, string>;
  /** Show auto-numbered labels. Default: true. */
  show?: boolean;
  /** Highlight rows that have a label. Default: false. */
  highlight?: boolean;
}

export interface ChatViewLinkingOptions {
  /** Show link UI on messages. Default: false. */
  enabled?: boolean;
  /** Build a shareable URL for a message id. */
  getMessageUrl?: (messageId: string) => string | undefined;
}

export interface ChatViewToolOptions {
  /** How tool calls render: full detail, inline summary, or hidden. Default: "complete". */
  callStyle?: ChatViewToolCallStyle;
  /** Collapse tool responses into the preceding assistant message. Default: true. */
  collapseToolMessages?: boolean;
  /** Override default tool call rendering for specific tools. */
  renderToolCall?: (
    props: import("./tools/ToolCallView").ToolCallViewProps
  ) => React.ReactNode | undefined;
}

export interface ContentTool {
  type: "tool";
  content: Exclude<Content, { type: "tool_use" }>[];
}

/**
 * Shallow guard for messages read out of unvalidated state blobs. ChatView
 * dispatches on `role` and tolerates the rest, so that is what it checks.
 */
export const isChatMessage = (value: unknown): value is ChatMessage =>
  isRecord(value) && typeof value["role"] === "string";
